import React, { useEffect, useState, useContext } from "react";
import RentalSchedule from "../../../components/Schedules/Others/RentalSchedule/RentalSchedule";
import { initSchedule } from "../../../utils/data/initData";
import { AuthContext } from "../../../contexts/AuthContext";
import { useErrorDialog } from "../../../contexts/ErrorDialogContext";
import { useSuccessDialog } from "../../../contexts/SuccessDialogContext";
import ComponentLoading from "../../../components/App/ComponentLoading";
import { handleScheduleDeletion } from "../utils/handleScheduleDeletion";
import { useAccessControl } from "../../../utils/hooks/useAccessControl";
import { PL_SCHEDULES } from "../../../utils/data/controlData";

const RentalSchedulePage = ({ company, scheduleId }) => {
  const { updatePLSchedule, getScheduleData, deleteScheduleByScheduleType } =
    useContext(AuthContext);
  const [saveTrigger, setSaveTrigger] = useState(false);
  const [schedule, setSchedule] = useState({});
  const [scheduleDetails, setScheduleDetails] = useState({});

  const { canEdit: canEditPLSchedule } = useAccessControl(PL_SCHEDULES);

  const [loading, setLoading] = useState(true);

  const handleError = useErrorDialog();
  const handleSuccess = useSuccessDialog();

  const saveSchedule = async (schedule, scheduleDetails) => {
    if (!company || !scheduleId) {
      handleError("Unexpected error happens. Please try again later.");
      return;
    }

    const updatedSchedule = {
      sch_id: scheduleId,
      scheduleType: "Rental",
      scheduleTitle: schedule.name,
      taxAdjustment: schedule.taxAdjustment,
      address_line1: scheduleDetails.address.address_line1,
      address_line2: scheduleDetails.address.address_line2,
      address_line3: scheduleDetails.address.address_line3,
      vacant_start: undefined,
      vacant_end: undefined,
      rental_start: undefined,
      rental_end: undefined,
      vacant_periods: scheduleDetails.vacant_periods,
      rental_periods: scheduleDetails.rental_periods,
      grossIncomeRows: scheduleDetails.grossIncomeRows,
      expensesRows: scheduleDetails.expensesRows,
      totalRevenue: scheduleDetails.totalRevenue,
      totalExpenses: scheduleDetails.totalExpenses,
      foreignTaxSuffered: scheduleDetails.foreignTaxSuffered,
      isTagToFTC: scheduleDetails.isTagToFTC,
      notes: schedule.notes,
      reviewNotes: schedule.reviewNotes,
      totalAdjustment: scheduleDetails.totalAdjustment,
      doc: scheduleDetails.doc,
    };

    setLoading(true);
    const response = await updatePLSchedule(updatedSchedule);
    setLoading(false);

    if (response) {
      handleSuccess("The schedule has been saved successfully.");
      setSaveTrigger((prev) => !prev);
    } else {
      handleError(
        "There was a problem saving the schedule. Please contact support if the issue persists."
      );
    }
  };

  const deleteSchedule = async () => {
    await handleScheduleDeletion({
      scheduleId,
      deleteFn: deleteScheduleByScheduleType,
      handleSuccess,
      handleError,
      setLoading,
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      if (!company || !scheduleId) {
        handleError(
          "We encountered an issue while fetching the data. Please refresh the page or try again shortly."
        );
        return;
      }
      //API call here
      const response = await getScheduleData(scheduleId, 'Rental');

      const scheduleData = response.schedule;

      //init
      if (scheduleData) {
        setSchedule(initSchedule(scheduleData, "Rental Income"));
        setScheduleDetails({
          address_line1: scheduleData.address_line1,
          address_line2: scheduleData.address_line2,
          address_line3: scheduleData.address_line3,
          vacant_start: scheduleData.vacant_start,
          vacant_end: scheduleData.vacant_end,
          rental_start: scheduleData.rental_start,
          rental_end: scheduleData.rental_end,
          vacant_periods: scheduleData.vacant_periods,
          rental_periods: scheduleData.rental_periods,
          grossIncomeRows: scheduleData.grossIncomeRows,
          expensesRows: scheduleData.expensesRows,
          shareOfExpenses: scheduleData.shareOfExpenses || { amount: 0, source: "" },
          foreignTaxSuffered: scheduleData.foreignTaxSuffered,
          isTagToFTC: scheduleData.isTagToFTC,
          schRef: scheduleData.schRef || null,
          foreignTaxCreditScheduleRef:
            scheduleData.foreignTaxCreditScheduleRef || null,
        });
      }

      setLoading(false);
    };

    fetchData();
  }, [saveTrigger, scheduleId, getScheduleData, updatePLSchedule, company.uen]);
  return (
    <>
      {loading ? (
        <ComponentLoading />
      ) : (
        <RentalSchedule
          company={company}
          schedule={schedule}
          scheduleDetails={scheduleDetails}
          saveSchedule={saveSchedule}
          deleteSchedule={deleteSchedule}
          canEditSchedule={canEditPLSchedule}
        />
      )}
    </>
  );
};

export default RentalSchedulePage;
